<template>
  <div v-if="context" :class="{ desktop: $isDesktop }">
    <translate tag="p" class="intro"> You are booking... </translate>
    <booking-campsite-details
      v-if="details"
      :address="details.address"
      :campsite-name="details.campsiteName"
      :last-booked="details.lastBooked"
      :photo="detailsPhoto"
      :photo-caption="details.photo && details.photo.caption"
      :rating="details.rating"
      class="centered"
    />

    <booking-stay-details
      v-if="context.quote"
      :details="details"
      :params="params"
      :quote="quote"
      class="centered"
    />

    <booking-form
      v-if="context.quote"
      class="booking-form"
      keep-alive
      :params="params"
      :user-contact-details="userContactDetails"
      :user-other-details="userOtherDetails"
      :details="details"
      :selected-extras="context.selectedExtras"
      :send="send"
      :context="context"
      :state-to-strings="stateToStrings"
      :quote="context.quote"
      :submit-errors="context.submitErrors"
    />
    <client-only>
      <state-strings-matches :state-to-strings="stateToStrings">
        <template #loading>
          <lazy-booking-loading-state />
        </template>
        <template #submitError>
          <lazy-booking-submit-error-state />
        </template>
        <template #loadingError>
          <lazy-booking-loading-error-state />
        </template>
      </state-strings-matches>
      <booking-message-modal
        v-if="context"
        :show-modal="state && state.matches('alreadyBooked')"
        :book-again-url="bookAgainUrlPath"
        :show-trigger="false"
        :message="context.error"
      />
      <booking-message-modal
        v-if="context"
        :show-modal="state && state.matches('allFormError')"
        :show-trigger="false"
        book-again-url="/"
        :message="context.error"
      >
        <template #header> &nbsp; </template>
      </booking-message-modal>
      <booking-message-modal
        v-if="context"
        :show-modal="state && state.matches('unknownError')"
        :book-again-url="bookAgainUrlPath"
        :show-trigger="false"
        :message="$t('Something went wrong, try again later')"
      />
    </client-only>
    <div id="teleport-target"></div>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import { isEqual } from 'lodash-es'
import { State } from 'xstate-v4'
import { UserPersonalDetails } from '~/apps/personal-details/types'
import { UserDetails } from '~/apps/user-details/types'
import AuthMixin from '~/mixins/AuthMixin'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { getURLSearchParams } from '~/utility/url-params'
import { LoggerPlugin } from '../../../plugins/logger'
import { logger } from '../../../utility/logger'
import type { BookingService } from '../machines/booking'
import { BookingStateEvent } from '../machines/types'
import type { QuoteRequestParams } from '../quote/schema'
import { getBookingService } from '../service'
import type { ContactDetails, OtherDetails } from '../submit/schema'

@Component({
  emits: ['selected-extras'],
  setup() {
    const { $log } = useNuxtApp()
    const formStore = process.client ? useFormStore(usePinia()) : undefined
    logger('using form store', formStore)
    const snackbar = process.client ? useSnackbar() : null
    return { formStore, log: $log, snackbar }
  },
  mixins: [AuthMixin],
})
class BookingMain extends Vue {
  declare isLoggedIn: boolean
  declare snackbar: ReturnType<typeof useSnackbar> | null
  declare formStore?: ReturnType<typeof useFormStore>
  declare log: LoggerPlugin

  @Prop()
  params: QuoteRequestParams

  @Prop()
  stateStr: string

  service: BookingService | null = null

  @Prop()
  initContext: BookingService['state']['context']

  context: BookingService['state']['context'] | null = null

  state: BookingService['state'] | null = null

  lastLoggedStripeTokenId: string | null = null

  get stateToStrings(): string[] {
    return this.state?.toStrings() || []
  }

  created() {
    // create a copy of the init context so we can modify it
    this.context = this.initContext
    void this.log('booking_main_created')
    this.lastLoggedStripeTokenId = null
  }

  get head() {
    return {
      title: (this.$t('Booking | {campsiteName}', {
        campsiteName: this.campsiteName,
      }) + ' | Pitchup.com') as string,
      meta: [
        this.ogImage,
        this.ogImageWidth,
        this.ogImageHeight,
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  }

  getService() {
    const selectedExtras =
      this.params.selectedExtras &&
      Object.keys(this.params.selectedExtras).length > 0
        ? this.params.selectedExtras
        : this.context?.quote?.extrasInitial || {}
    let startState
    try {
      startState = JSON.parse(this.stateStr)
    } catch (error) {
      console.error('[BookingMain] Failed to parse booking state:', {
        error: error instanceof Error ? error.message : String(error),
      })
      throw new Error('Failed to load booking state. Please refresh the page.')
    }
    startState.context.selectedExtras = selectedExtras
    return getBookingService(
      {
        ...this.params,
        selectedExtras: selectedExtras,
      },
      (this.$route.params.lang as string) || 'en-gb',
    ).start(State.create(startState))
  }

  async mounted() {
    void this.log('booking_main_mounted')
    this.service = this.getService()
    this.context = this.service.state.context
    this.state = this.service.state
    this.service.onTransition(async (state, event) => {
      logger('BookingMain.onTransition', state.toStrings(), event.type)
      void this.log('booking_main_transition', {
        state: state.toStrings(),
        eventType: event.type,
        context: state.context,
        stateEvent: event,
      })
      if (
        event.type === 'UPDATE_CONTACT_DETAILS' &&
        !isEqual(this.formStore?.formData.contactDetails, event.details)
      ) {
        this.formStore?.updateField('contactDetails', event.details)
      }
      if (
        event.type === 'UPDATE_OTHER_DETAILS' &&
        !isEqual(this.formStore?.formData.otherDetails, event.details)
      ) {
        this.formStore?.updateField('otherDetails', event.details)
      }
      if (event.type === 'EXTRAS_CHANGED') {
        if (
          !isEqual(
            this.formStore?.formData.selectedExtras,
            state.context.selectedExtras,
          )
        ) {
          this.formStore?.updateField(
            'selectedExtras',
            state.context.selectedExtras,
          )
        }
        this.$emit('selected-extras', state.context.selectedExtras)
      }
      await this.onStateChange(state)
    })
    if ('error_msg' in this.$route.query) {
      await this.showNotificationError(this.$route.query.error_msg as string)
      await this.stripErrorParamsFromUrl()
    } else if ('error' in this.$route.query) {
      await this.showNotificationError('Payment authentication error')
      await this.stripErrorParamsFromUrl()
    }
    if (this.isLoggedIn) {
      await this.handleOnLogIn()
    }
    await this.onStateChange(this.state)
    this.restoreFormFields()
  }

  async onStateChange(state: BookingService['state']) {
    this.context = state.context
    this.state = state
    if (state.matches('requestError')) {
      this.$emit('request-error', this.context?.details)
    }
    if (state.matches('campsiteNotFoundError')) {
      this.$emit('campsite-not-found-error')
    }
    if (state.matches('submitServerError')) {
      this.showSubmitServerError()
    }

    // Track when a Stripe token becomes available
    if (
      this.context?.stripeToken &&
      (!this.lastLoggedStripeTokenId ||
        this.lastLoggedStripeTokenId !== this.context.stripeToken.customer_id)
    ) {
      this.lastLoggedStripeTokenId =
        this.context.stripeToken.customer_id || null
      void this.log('stripe_token_available_in_context', {
        customerId: this.context.stripeToken.customer_id,
        state: state.toStrings().join(','),
      })
    }

    const langPrefix =
      this.$i18n.locale !== 'en-gb' ? `/${this.$i18n.locale}` : ''
    if (
      state.matches('processing') &&
      this.context?.bookingRequest?.paymentDetails.method !== 'paypal'
    ) {
      const path = `${langPrefix}/booking/payment/${this.context?.taskId}/${this.context?.bookingId}`
      await this.$router.push({ path })
    }
  }

  send(event: BookingStateEvent) {
    this.service && this.service.send(event)
  }

  restoreFormFields() {
    if (this.formStore?.formData.contactDetails) {
      this.send({
        type: 'UPDATE_CONTACT_DETAILS',
        details: this.formStore?.formData.contactDetails as ContactDetails,
      })
    }
    if (this.formStore?.formData.otherDetails) {
      this.send({
        type: 'UPDATE_OTHER_DETAILS',
        details: this.formStore?.formData.otherDetails as OtherDetails,
      })
    }
    // Only restore extras from localStorage if URL didn't provide any
    if (
      this.formStore?.formData.selectedExtras &&
      (!this.params.selectedExtras ||
        Object.keys(this.params.selectedExtras).length === 0)
    ) {
      const selectedExtras = Object.keys(
        this.formStore?.formData.selectedExtras,
      ).map((extraId) => ({
        extraId,
        quantity: this.formStore?.formData?.selectedExtras?.[extraId] || 0,
      }))
      this.send({
        type: 'EXTRAS_CHANGED',
        selected: selectedExtras,
      })
    }
  }

  @Watch('isLoggedIn')
  async handleAuthStateChange(isLoggedIn: boolean) {
    isLoggedIn ? await this.handleOnLogIn() : this.handleOnLogOut()
  }

  private async handleOnLogIn() {
    await this.fetchUserDetails()
    await this.fetchUserPersonalDetails()
  }

  private handleOnLogOut() {
    Object.keys(this.userDetails || {}).forEach(
      (key) => (this.userDetails![key] = ''),
    )
    Object.keys(this.userPersonalDetails || {}).forEach(
      (key) => (this.userPersonalDetails![key] = ''),
    )
  }

  private showSubmitServerError() {
    this.snackbar?.add({
      text: this.$t('Booking Server Error'),
      type: 'success',
      dismissible: false,
    })
  }

  private async showNotificationError(errorMsg: string) {
    await this.$nextTick()
    this.snackbar?.add({
      text: this.$t(errorMsg),
      type: 'success',
      dismissible: false,
    })
  }

  private stripErrorParamsFromUrl() {
    return this.$router.replace({
      ...this.$route,
      query: {
        ...this.$route.query,
        error: undefined,
        error_msg: undefined,
      },
    })
  }

  get bookAgainUrlPath() {
    const path = this.details ? `${this.details.path}${this.details.slug}` : ''
    const queryParams = getURLSearchParams({
      pitchtype: this.params.pitchtypeId,
      adults: this.params.party.adults,
      childAges: this.params.party.childAges,
    })
    return `/campsites/${path}/?${queryParams}`
  }

  userDetails: UserDetails | null = null

  private async fetchUserDetails() {
    this.userDetails = await $fetch<UserDetails>(
      '/_/fallback/_/users/user-details/',
    )
  }

  userPersonalDetails: UserPersonalDetails | null = null

  private async fetchUserPersonalDetails() {
    this.userPersonalDetails = await $fetch<UserPersonalDetails>(
      '/_/fallback/_/users/user-personal-details/',
    )
  }

  get userContactDetails(): ContactDetails {
    return {
      address: {
        city: this.userPersonalDetails?.city || '',
        country: this.userPersonalDetails?.country || '',
        postCode: this.userPersonalDetails?.postcode || '',
        street: this.userPersonalDetails?.address || '',
      },
      email: this.userPersonalDetails?.email || '',
      firstName: this.userDetails?.firstName || '',
      lastName: this.userDetails?.lastName || '',
      phone: this.userPersonalDetails?.telephone || {
        countryDialingCode: '',
        number: '',
      },
      sendTextMessage: false,
    }
  }

  get userOtherDetails(): OtherDetails {
    return {
      unitType: this.userPersonalDetails?.unitType?.id,
      unitWidth: this.userPersonalDetails?.dimensionsWidth,
      unitDepth: this.userPersonalDetails?.dimensionsDepth,
      carRegistration: this.userPersonalDetails?.carRegistrationNumber,
      partyNames: this.userPersonalDetails?.namesOfAllPartyMembers,
      groupName: '',
      specialRequests: '',
      estimatedTimeOfArrival: '',
    }
  }

  get signature(): string {
    return this.context?.paymentStatus?.result?.signature || ''
  }

  get campsiteName() {
    return this.details?.campsiteName
  }

  get details() {
    return this.context?.details
  }

  get detailsPhoto() {
    return this.context?.details?.photo && this.context?.details.photo.url
      ? this.context.details.photo
      : undefined
  }

  get quote() {
    return this.context?.quote
  }

  get ogImage() {
    return {
      hid: 'og:image',
      property: 'og:image',
      content: this.details?.photo?.url.masterImage || '',
    }
  }

  get ogImageWidth() {
    return {
      hid: 'og:image:width',
      property: 'og:image:width',
      content: '180',
    }
  }

  get ogImageHeight() {
    return {
      hid: 'og:image:height',
      property: 'og:image:height',
      content: '180',
    }
  }
}

export default toNative(BookingMain)
</script>
<style scoped>
.booking-state {
  background: white;
  bottom: 0;
  left: 0;
  min-height: 100vh;
  padding: 1em;
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  z-index: 9;
  text-align: center;
}

.booking-state :deep(header) {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 1.5em;
  width: 100%;
}

.booking-state :deep(header img) {
  width: auto;
  height: 2em;
}

.intro {
  text-align: center;
  font-style: italic;
  font-size: var(--body);
  color: var(--colour-text-light);
}

/* desktop & tablet */
.desktop .centered,
.desktop .centered.pt-details,
.desktop .booking-form :deep(form > section) {
  margin: auto;
  max-width: var(--legacy-page-width-max);
  padding: 2em 0;
  width: 100%;
  font-size: inherit;
}

.desktop .centered.pt-details {
  margin: auto auto 3em;
  border-radius: 0.6em;
  padding: 2em;
}

.desktop .booking-form :deep(p),
.desktop .booking-form :deep(label),
.desktop .booking-form :deep(table),
.desktop .booking-form :deep(.payments-due tr.info),
.desktop .booking-form :deep(.tax-terms),
.desktop .booking-form :deep(table tr),
.desktop .booking-form :deep(li) {
  font-size: revert;
}

.desktop .booking-form :deep(.help-text) {
  font-size: var(--body);
}

.desktop .pt-details :deep(.converted-ages) {
  font-size: var(--body);
}

.desktop .booking-form :deep(label) {
  display: block;
  margin-top: 1.2em;
}

.desktop .booking-form :deep(.pay-all-now label),
.desktop .booking-form :deep(.payments-method-form label) {
  margin-top: 0;
}

.desktop .booking-form :deep(form > section.payment),
.desktop .booking-form :deep(form > section.please-note) {
  padding: 2em;
  border-radius: 0.6em;
  margin: 2em auto;
}

.desktop .booking-form :deep(form > section.payment) {
  margin-bottom: 0;
}

/* small tablets */
@media (max-width: 768px) {
  .desktop .centered,
  .desktop .centered.pt-details,
  .desktop .booking-form :deep(form > section) {
    max-width: 90%;
  }
}
</style>
